import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Loader2, AlertCircle } from 'lucide-react';
import ResultCard from '../components/ResultCard';
import VerifiedDirectory from '../components/VerifiedDirectory';
import ThemeToggle from '../components/ThemeToggle';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import BrandLogo from '../components/BrandLogo';

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [error, setError] = useState(null);
  const [cameFromHistory, setCameFromHistory] = useState(false);

  useEffect(() => {
    // Check if user has premium subscription
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user && (user.subscription_tier === 'pro' || user.subscription_tier === 'enterprise')) {
      setIsPremium(true);
    }

    // Get result from navigation state
    if (location.state?.result) {
      setResult(location.state.result);
      // Check if came from history page
      setCameFromHistory(location.state?.fromHistory === true);
    } else if (location.state?.error) {
      setError(location.state.error);
    } else {
      // No data, redirect home
      navigate('/');
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {cameFromHistory && (
              <Button
                onClick={() => navigate('/history')}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors duration-200 text-xs sm:text-sm"
              >
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">Back to History</span>
                <span className="sm:hidden">History</span>
              </Button>
            )}
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 sm:gap-2 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors duration-200 text-xs sm:text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">New Search</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Results Content */}
      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-blue-600 animate-spin mb-4 sm:mb-6" />
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-700 dark:text-slate-200 mb-2">Kai is Analyzing...</h2>
            <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">Kai is checking multiple sources to verify this information</p>
          </div>
        ) : error ? (
          <div className="max-w-4xl mx-auto">
            <Alert className="bg-red-50 dark:bg-red-900/40 border-red-200 dark:border-red-700">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <AlertTitle className="text-red-800 dark:text-red-200 font-semibold">Error</AlertTitle>
              <AlertDescription className="text-red-700 dark:text-red-300">
                {error}. Please try again.
              </AlertDescription>
            </Alert>
            <div className="mt-6 text-center">
              <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700">
                Try Another Search
              </Button>
            </div>
          </div>
        ) : result ? (
          <div className="flex flex-col lg:flex-row gap-6 max-w-6xl mx-auto">
            {/* Main Content */}
            <div className="flex-1 min-w-0 space-y-4 sm:space-y-6">
              <div className="mb-4 sm:mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100 mb-1 sm:mb-2">Verification Results</h1>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">Based on analysis of multiple trusted sources</p>
              </div>

              <ResultCard result={result} />
              
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200 dark:border-blue-800">
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">About This Result</h3>
                <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm leading-relaxed mb-2">
                  This verification was performed by Kai using AI analysis combined with web scraping of trusted sources. 
                  Results are continuously improved based on new information. For critical decisions, please verify 
                  with multiple independent sources.
                </p>
                <p className="text-slate-500 dark:text-slate-400 text-xs">
                  AI Provider: <span className="font-semibold">{result.ai_provider}</span>
                </p>
              </div>
            </div>

            {/* Sidebar - Verified Directory for Free Users */}
            {!isPremium && (
              <div className="w-full lg:w-80 flex-shrink-0">
                <div className="lg:sticky lg:top-24">
                  <VerifiedDirectory 
                    variant="sidebar" 
                    onUpgradeClick={() => navigate('/pricing')}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-400">No results found</p>
            <Button onClick={() => navigate('/')} className="mt-4 bg-blue-600 hover:bg-blue-700">
              Go Back Home
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ResultsPage;